import styled from 'styled-components';

import { media } from '../../utils/mediaQueries';

import { LocaleLink } from '@/utils/locale';

export const QuickAccessGrid = styled.div<{ $gap: number }>`
  display: grid;
  width: 100%;
  max-width: 800px;
  overflow: hidden;
  grid-template-columns: 1fr;
  gap: ${({ $gap }) => $gap}px;
  margin: 0 auto;
  padding: 0;

  ${media.tablet} {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 2px;
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 0;
  }
`;

export const QuickAccessCard = styled(LocaleLink)`
  position: relative;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : 'white')};
  border-radius: 6px;
  padding: 16px;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e2e8f0')};
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e2e8f0')};
  }

  ${media.tablet} {
    padding: 10px;
  }

  ${media.mobile} {
    padding: 12px;
    border-radius: 6px;
  }
`;

export const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 45px;
  height: 45px;
  border-radius: 6px;
  background: ${({ theme }) => (theme.mode === 'dark'
    ? 'linear-gradient(135deg, #374151, #4b5563)'
    : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  ${media.tablet} {
    width: 38px;
    height: 38px;
    border-radius: 6px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  ${media.mobile} {
    width: 40px;
    height: 40px;
    border-radius: 6px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const CardTitle = styled.h3`
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1c2b33')};
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
  position: relative;
  display: inline-block;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#1c2b33')};
    transition: width 0.2s ease;
  }

  ${QuickAccessCard}:hover & {
    &::after {
      width: 100%;
    }
  }

  ${media.tablet} {
    font-size: 13px;
  }

  ${media.mobile} {
    font-size: 12px;
    font-weight: 300;
  }
`;

export const CardDesc = styled.p`
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#64748b')};
  font-size: 11px;
  line-height: 1.5;

  ${media.tablet} {
    font-size: 10px;
    line-height: 1.4;
  }

  ${media.mobile} {
    font-size: 10px;
    line-height: 1.3;
  }
`;

export const StatusBadge = styled.span<{ $status: 'new' | 'update' }>`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: ${({ $status }) => ($status === 'new'
    ? 'linear-gradient(135deg, #ef4444, #f43f5e)'
    : 'linear-gradient(135deg, #3b82f6, #6366f1)')};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  ${media.tablet} {
    padding: 3px 8px;
    font-size: 10px;
    top: 8px;
    left: 8px;
  }

  ${media.mobile} {
    padding: 2px 6px;
    font-size: 9px;
    top: 6px;
    left: 6px;
  }
`;

export const AvailableBadge = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#64748b')};

  ${media.tablet} {
    font-size: 10px;
    gap: 4px;
    bottom: 6px;
    right: 6px;
    padding: 3px 6px;
  }

  ${media.mobile} {
    font-size: 9px;
    gap: 3px;
    bottom: auto;
    top: 4px;
    right: 4px;
    padding: 2px 4px;
  }
`;

export const CardContent = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  ${media.tablet} {
    gap: 10px;
  }

  ${media.mobile} {
    gap: 10px;
    align-items: center;
  }
`;

export const ContentWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 2px;

  ${media.mobile} {
    padding-top: 0;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 3px;

  ${media.tablet} {
    margin-bottom: 2px;
    gap: 6px;
  }

  ${media.mobile} {
    margin-bottom: 1px;
    gap: 4px;
  }
`;

export const ChevronWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#94a3b8')};

  ${media.mobile} {
    display: none;
  }
`;

export const IconInner = styled.div`
  color: ${({ theme }) => (theme.mode === 'dark' ? '#e2e8f0' : '#475569')};
`;

export const CountBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 300;
  background: ${({ theme }) => (theme.mode === 'dark'
    ? 'linear-gradient(135deg, #1a4d4d, #0a2626)'
    : 'linear-gradient(135deg, #1a4d4d, #f59e0b)')};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

  ${media.tablet} {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    font-size: 11px;
    top: 8px;
    right: 8px;
  }

  ${media.mobile} {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-size: 10px;
    top: 6px;
    right: 6px;
  }
`;
